import { UserInfoFI } from "@/components/HolderForm/UserInfoFI.ts";
import { UserInfoFormValues } from "@/components/HolderForm/UserInfoFormValues.ts";
import { RefObject, useEffect } from "react";
import { UseFormSetValue } from "react-hook-form";

const AUTOFILL_FIELDS: UserInfoFI[] = [
  UserInfoFI.FirstName,
  UserInfoFI.LastName,
  UserInfoFI.Address,
  UserInfoFI.PostalCode,
  UserInfoFI.City,
  UserInfoFI.Email,
];

/**
 * Syncs browser autofill to React Hook Form.
 *
 * Chrome fills DOM values on multiple inputs but only fires change/input events
 * on the focused field. React's synthetic events (especially inside Shadow DOM)
 * don't pick up the changes on unfocused inputs.
 *
 * Detection strategies (layered):
 * 1. `animationstart` — Chrome applies `:-webkit-autofill` which triggers a CSS
 *    animation (`onAutoFillStart`). Fires per-input AFTER the value is set.
 * 2. `change` events (capture phase) — catches autofill and password manager fills
 *    that dispatch real DOM events.
 * 3. Native `input` events — catches non-keyboard input events (password managers,
 *    paste, programmatic fills).
 * 4. Polling fallback — checks for `:-webkit-autofill` pseudo-class every 500 ms
 *    as a final safety net (stops after 10 seconds).
 *
 * All strategies funnel into a debounced `syncAllFields` that pushes every field's
 * current DOM value into RHF.
 */
export const useAutofillSync = (
  formRef: RefObject<HTMLFormElement | null>,
  setValue: UseFormSetValue<UserInfoFormValues>,
) => {
  useEffect(() => {
    const form = formRef.current;
    if (!form) return;

    let syncTimer: ReturnType<typeof setTimeout>;

    const syncAllFields = () => {
      for (const field of AUTOFILL_FIELDS) {
        const input = form.querySelector<HTMLInputElement>(`[name="${field}"]`);
        if (input?.value) {
          setValue(field, input.value, { shouldValidate: true, shouldDirty: true });
        }
      }
    };

    const debouncedSync = () => {
      clearTimeout(syncTimer);
      syncTimer = setTimeout(syncAllFields, 50);
    };

    // ── 1. CSS animation on :-webkit-autofill ───────────────────────────
    const onAnimation = (e: AnimationEvent) => {
      if (e.animationName === "onAutoFillStart") debouncedSync();
    };

    // ── 2. Change events (capture phase) ────────────────────────────────
    const onChange = () => debouncedSync();

    // ── 3. Non-keyboard input events ────────────────────────────────────
    const onInput = (e: Event) => {
      const ie = e as InputEvent;
      if (ie.inputType === "insertText" || ie.inputType === "deleteContentBackward") return;
      debouncedSync();
    };

    // ── 4. Polling fallback ─────────────────────────────────────────────
    let pollCount = 0;
    const pollId = setInterval(() => {
      pollCount++;
      if (pollCount > 20) {
        clearInterval(pollId);
        return;
      }
      for (const field of AUTOFILL_FIELDS) {
        const input = form.querySelector<HTMLInputElement>(`[name="${field}"]`);
        if (!input) continue;
        try {
          if (input.matches(":-webkit-autofill")) {
            syncAllFields();
            clearInterval(pollId);
            return;
          }
        } catch {
          clearInterval(pollId);
          return;
        }
      }
    }, 500);

    form.addEventListener("animationstart", onAnimation);
    form.addEventListener("change", onChange, true);
    form.addEventListener("input", onInput, true);

    return () => {
      clearTimeout(syncTimer);
      clearInterval(pollId);
      form.removeEventListener("animationstart", onAnimation);
      form.removeEventListener("change", onChange, true);
      form.removeEventListener("input", onInput, true);
    };
  }, [formRef, setValue]);
};
